import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { fetchCategoryNeighbours } from '@modules/product/util/fetch-category-neighbours.server';
import type { CategoryNeighbours } from '@modules/product/util/fetch-category-neighbours.server';

export type {
    CategoryNeighbour,
    CategoryNeighbours,
} from '@modules/product/util/fetch-category-neighbours.server';

export const PRODUCT_NEIGHBOURS_API_ROUTE = '/remix-api/product/neighbours';

/**
 * How long the browser may reuse an answer.
 *
 * The lookup costs a search call per page until it finds the product, and a
 * product deep in a large category — or one that is not in that listing at
 * all — pays that in full every time the answer is asked for. A category's
 * order changes on the scale of a merchandising edit, not a page view, so a
 * few minutes of reuse costs nothing in freshness and takes the repeat visit
 * and the second tab off the search service entirely. `private`: the listing
 * is read as the directory serves it, but it is still answered per reader.
 */
const NEIGHBOURS_CACHE_CONTROL = 'private, max-age=300';

/** The neighbours, marked with the product they were looked up for. */
export interface ProductNeighboursAnswer extends CategoryNeighbours {
    /** Echoes the asking product, so a stale answer is never laid out under another product's foot. */
    for: number;
}

/**
 * The products either side of one in its category listing, for the foot of
 * its page: the next-product turn and the island's next-product offer.
 *
 * A resource route rather than a field of the product loader on purpose.
 * The lookup reads the category's listing a hundred products at a time until
 * it finds where this one sits, and in the loader it sat on every product
 * page's first byte — paid by every reader, for a foot most never reach.
 * Fetched from the client instead, it arrives long before the reader is
 * anywhere near the foot, and the page's own data — including the copy the
 * turn warms — stays that much lighter.
 */
export const loader = async ({ request, context }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const categoryLink = url.searchParams.get('category') ?? '';
    const productId = Number(url.searchParams.get('product'));
    if (!categoryLink || !Number.isInteger(productId) || productId <= 0) {
        throw new Response('Missing category or product', { status: 400 });
    }

    const neighbours = await fetchCategoryNeighbours({
        context,
        categoryLink,
        currentProductId: productId,
    });
    const answer: ProductNeighboursAnswer = { for: productId, ...neighbours };
    return routerData(answer, {
        headers: { 'Cache-Control': NEIGHBOURS_CACHE_CONTROL },
    });
};
